import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { fetchDeviceSnapshotBlob, fetchDeviceStream } from '../../api/hikvision'
import { Go2RtcPlayer, buildStreamWsUrl } from '../../lib/videoRTC'

const CHANNEL_OPTIONS = [
  { id: '101', label: 'Main (101)', description: '720p, ~2 Mbps — основной поток' },
  { id: '102', label: 'Sub (102)', description: '720p, ~1 Mbps' },
]

const stateLabels = {
  idle: 'Ожидание',
  connecting: 'Подключение…',
  live: 'Live',
  snapshot: 'Снимок (MJPEG)',
  reconnecting: 'Переподключение…',
  error: 'Ошибка',
  stopped: 'Остановлено',
}

const HikvisionLiveVideo = ({
  deviceId,
  videoEnabled,
  defaultChannel = '101',
  /** NVR: camera number; profile sub|main */
  nvrMode = false,
  profile = 'sub',
  className = '',
}) => {
  const videoRef = useRef(null)
  const playerRef = useRef(null)
  const snapshotTimerRef = useRef(null)
  const snapshotUrlRef = useRef(null)

  const [channel, setChannel] = useState(defaultChannel)
  const [playerState, setPlayerState] = useState('idle')
  const [stateDetail, setStateDetail] = useState('')
  const [isLiveActive, setIsLiveActive] = useState(false)
  const [posterUrl, setPosterUrl] = useState(null)
  const [error, setError] = useState(null)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const containerRef = useRef(null)

  const streamOptions = useMemo(
    () => (nvrMode ? { profile } : undefined),
    [nvrMode, profile]
  )

  const revokePoster = useCallback(() => {
    if (snapshotUrlRef.current) {
      URL.revokeObjectURL(snapshotUrlRef.current)
      snapshotUrlRef.current = null
    }
  }, [])

  const failCountRef = useRef(0)

  const loadPoster = useCallback(async () => {
    if (!deviceId || !videoEnabled) return false
    try {
      const blobUrl = await fetchDeviceSnapshotBlob(deviceId, channel, streamOptions)
      const prev = snapshotUrlRef.current
      snapshotUrlRef.current = blobUrl
      setPosterUrl(blobUrl)
      if (prev && prev !== blobUrl) URL.revokeObjectURL(prev)
      failCountRef.current = 0
      return true
    } catch {
      failCountRef.current += 1
      const prev = snapshotUrlRef.current
      snapshotUrlRef.current = null
      setPosterUrl(null)
      if (prev) URL.revokeObjectURL(prev)
      return false
    }
  }, [deviceId, videoEnabled, channel, streamOptions])

  const stopSnapshotPolling = useCallback(() => {
    if (snapshotTimerRef.current) {
      clearTimeout(snapshotTimerRef.current)
      snapshotTimerRef.current = null
    }
  }, [])

  const startSnapshotPolling = useCallback(() => {
    stopSnapshotPolling()
    const tick = async () => {
      const ok = await loadPoster()
      const delay = ok ? 3000 : Math.min(15000 * Math.max(failCountRef.current, 1), 60000)
      snapshotTimerRef.current = setTimeout(tick, delay)
    }
    tick()
  }, [loadPoster, stopSnapshotPolling])

  const stopLive = useCallback(() => {
    if (playerRef.current) {
      playerRef.current.destroy()
      playerRef.current = null
    }
    setIsLiveActive(false)
    setPlayerState('stopped')
  }, [])

  const startLive = useCallback(async () => {
    if (!deviceId || !videoEnabled || !videoRef.current) return

    setError(null)
    setPlayerState('connecting')
    stopSnapshotPolling()
    stopLive()

    try {
      const streamInfo = await fetchDeviceStream(deviceId, channel, streamOptions)
      const wsUrl = buildStreamWsUrl(streamInfo.wsUrl)

      const player = new Go2RtcPlayer(videoRef.current, {
        mode: 'webrtc,mjpeg',
        media: 'video',
        onStateChange: (state, detail) => {
          setPlayerState(state)
          setStateDetail(detail || '')
          if (state === 'error') {
            setError(detail || 'Не удалось установить видеосоединение')
            startSnapshotPolling()
          }
        },
      })

      playerRef.current = player
      setIsLiveActive(true)
      player.connect(wsUrl)
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Ошибка запуска видео')
      setPlayerState('error')
      startSnapshotPolling()
    }
  }, [deviceId, videoEnabled, channel, streamOptions, stopLive, stopSnapshotPolling, startSnapshotPolling])

  useEffect(() => {
    setChannel(defaultChannel)
  }, [defaultChannel])

  useEffect(() => {
    if (!videoEnabled) return undefined
    loadPoster()
    return () => {
      stopSnapshotPolling()
      stopLive()
      revokePoster()
    }
  }, [videoEnabled, loadPoster, stopSnapshotPolling, stopLive, revokePoster])

  useEffect(() => {
    if (isLiveActive) {
      startLive()
    } else if (videoEnabled) {
      loadPoster()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channel])

  const toggleFullscreen = async () => {
    const el = containerRef.current
    if (!el) return
    if (!document.fullscreenElement) {
      await el.requestFullscreen?.()
      setIsFullscreen(true)
    } else {
      await document.exitFullscreen?.()
      setIsFullscreen(false)
    }
  }

  if (!videoEnabled) {
    return (
      <div
        className={`flex aspect-video items-center justify-center rounded-2xl border border-dashed border-surface-border/60 bg-surface-base/40 text-sm text-muted-400 ${className}`}
      >
        <div className="flex flex-col items-center gap-2 px-6 text-center">
          <span className="material-icons text-3xl text-muted-500">videocam_off</span>
          <span>Видео не включено для этого устройства</span>
        </div>
      </div>
    )
  }

  return (
    <div
      ref={containerRef}
      className={`relative overflow-hidden rounded-2xl border border-surface-border/60 bg-black shadow-elevated ${className}`}
    >
      <div className="relative aspect-video w-full bg-black">
        {!isLiveActive && posterUrl && (
          <img
            src={posterUrl}
            alt="Снимок камеры"
            className="absolute inset-0 h-full w-full object-cover"
          />
        )}
        <video
          ref={videoRef}
          className={`h-full w-full object-contain ${
            isLiveActive || playerState === 'connecting' ? 'block' : 'hidden'
          }`}
          playsInline
          muted
          autoPlay
        />

        {!isLiveActive && !posterUrl && playerState !== 'connecting' && (
          <div className="absolute inset-0 flex items-center justify-center bg-surface-base/80">
            <span className="material-icons animate-pulse text-4xl text-muted-400">videocam</span>
          </div>
        )}

        <div className="absolute left-3 top-3 flex flex-wrap items-center gap-2">
          <span
            className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider ${
              playerState === 'live'
                ? 'bg-positive/20 text-positive'
                : playerState === 'error'
                ? 'bg-critical/20 text-critical'
                : 'bg-black/50 text-white backdrop-blur-sm'
            }`}
          >
            {playerState === 'live' && (
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-positive/60" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-positive" />
              </span>
            )}
            {stateLabels[playerState] || playerState}
          </span>
        </div>

        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-3 pt-8">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex flex-wrap items-center gap-2">
              {!nvrMode && CHANNEL_OPTIONS.map((opt) => (
                <button
                  key={opt.id}
                  type="button"
                  title={opt.description}
                  onClick={() => setChannel(opt.id)}
                  className={`rounded-xl px-2.5 py-1 text-xs font-semibold transition ${
                    channel === opt.id
                      ? 'bg-brand-400/30 text-brand-100'
                      : 'bg-black/40 text-muted-300 hover:bg-black/60 hover:text-white'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
              {nvrMode && (
                <span className="rounded-xl bg-black/40 px-2.5 py-1 text-xs font-semibold text-muted-300">
                  Канал {channel} · {profile === 'main' ? 'основной' : 'суб'}
                </span>
              )}
            </div>

            <div className="flex items-center gap-2">
              {!isLiveActive ? (
                <button
                  type="button"
                  onClick={startLive}
                  className="inline-flex items-center gap-1.5 rounded-xl bg-brand-400/25 px-3 py-1.5 text-xs font-semibold text-brand-100 transition hover:bg-brand-400/40"
                >
                  <span className="material-icons text-sm">play_arrow</span>
                  Live
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => {
                    stopLive()
                    startSnapshotPolling()
                  }}
                  className="inline-flex items-center gap-1.5 rounded-xl bg-black/40 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-black/60"
                >
                  <span className="material-icons text-sm">stop</span>
                  Стоп
                </button>
              )}
              <button
                type="button"
                onClick={toggleFullscreen}
                className="inline-flex items-center justify-center rounded-xl bg-black/40 p-1.5 text-white transition hover:bg-black/60"
                title="Полный экран"
              >
                <span className="material-icons text-sm">
                  {isFullscreen ? 'fullscreen_exit' : 'fullscreen'}
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div className="border-t border-critical/30 bg-critical/10 px-4 py-2 text-xs text-critical">
          {error}
          {stateDetail ? ` — ${stateDetail}` : ''}
        </div>
      )}
    </div>
  )
}

export default HikvisionLiveVideo
